import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import Switch from '@mui/material/Switch';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import logo from '../assets/readlinker-logo.svg';
import logoSmall from '../assets/images/readLinker_mini_logo_128.png';
import { getUserProfileImageByuserSubCF } from '../utils/userProfileApi';
import { useThemeMode } from '../context/ThemeModeContext';
import AccountSwitcherMenu from './AccountSwitcherMenu';

const Header: React.FC = () => {
  const { isLoggedIn, userSub, loggedInAccounts } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  // アカウント切替でuserSubが変わるたびに追従させる必要があるため、
  // アカウント登録済みの一覧（cognito:username由来で確実に正しい）から都度導出する。
  // 以前は isLoggedIn だけを見て一度だけセッションを取得していたため、
  // 切り替えてもヘッダーのアバター画像だけ古いアカウントのまま残るバグがあった。
  const username = loggedInAccounts.find((a) => a.sub === userSub)?.username ?? null;

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode, toggleMode } = useThemeMode();

  useEffect(() => {
    if (!location.pathname.startsWith('/search')) {
      setSearchQuery('');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isLoggedIn || !userSub) {
      setProfileImage(null);
      return;
    }
    getUserProfileImageByuserSubCF(userSub)
      .then(img => setProfileImage(img || null))
      .catch(() => setProfileImage(null));
  }, [isLoggedIn, userSub]);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    navigate(`/search/${searchQuery}`);
    window.scrollTo(0, 0);
    setIsSearchActive(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgb(35,48,72)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          zIndex: theme => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 64, display: 'flex', justifyContent: 'space-between' }}>
          {/* ロゴ */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={isSmallScreen ? logoSmall : logo}
              alt="ReadLinker"
              style={{
                height: isSmallScreen ? 28 : 36,
                display: 'block',
              }}
            />
          </Link>

          {/* 検索 */}
          <Box sx={{ flexGrow: 1, mx: 2, display: 'flex', justifyContent: 'center' }}>
            <TextField
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              onFocus={() => setIsSearchActive(true)}
              placeholder="タイトル・著者名で検索"
              size="small"
              sx={{
                width: '100%',
                maxWidth: 520,
                backgroundColor: 'rgba(255,255,255,0.92)',
                borderRadius: 1.5,
                '& fieldset': {
                  borderColor: 'rgba(0,0,0,0.15)',
                },
                '& input': {
                  color: 'rgba(0,0,0,0.87)',
                },
                '& input::placeholder': {
                  color: 'rgba(0,0,0,0.5)',
                  opacity: 1,
                },
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch} sx={{ color: 'rgba(0,0,0,0.6)' }}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* 右側 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoggedIn && !isSmallScreen && (
              <Button color="inherit" component={Link} to="/bookshelf">
                本棚
              </Button>
            )}

            {isLoggedIn && profileImage && (
              <img
                src={profileImage}
                alt="profile"
                onClick={e => setAccountMenuAnchorEl(e.currentTarget)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                }}
              />
            )}

            <IconButton color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* メニュー */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {isLoggedIn && <MenuItem component={Link} to="/bookshelf" onClick={() => setAnchorEl(null)}>本棚</MenuItem>}
        {isLoggedIn && <MenuItem component={Link} to="/bibliography" onClick={() => setAnchorEl(null)}>書誌管理</MenuItem>}
        {isLoggedIn && <MenuItem component={Link} to="/shelves/manage" onClick={() => setAnchorEl(null)}>本棚編集</MenuItem>}
        {isLoggedIn && <MenuItem component={Link} to="/favBooks" onClick={() => setAnchorEl(null)}>お気に入り</MenuItem>}
        {isLoggedIn && <MenuItem component={Link} to="/mylist" onClick={() => setAnchorEl(null)}>リスト</MenuItem>}
        {isLoggedIn && <MenuItem component={Link} to="/highlights" onClick={() => setAnchorEl(null)}>抜粋・メモ</MenuItem>}
        {isLoggedIn && username && <MenuItem component={Link} to={`/${username}`} onClick={() => setAnchorEl(null)}>マイプロフィール</MenuItem>}
        <Divider />
        <MenuItem component={Link} to="/about-readlinker" onClick={() => setAnchorEl(null)}>ReadLinkerについて</MenuItem>
        {!isLoggedIn && <MenuItem component={Link} to="/login" onClick={() => setAnchorEl(null)}>ログイン</MenuItem>}
        <MenuItem
          onClick={() => { toggleMode(); setAnchorEl(null); }}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode === 'dark' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
            {mode === 'dark' ? 'ダーク' : 'ライト'}
          </Box>
          <Switch
            size="small"
            checked={mode === 'light'}
            onClick={(e) => e.stopPropagation()}
            onChange={() => { toggleMode(); setAnchorEl(null); }}
          />
        </MenuItem>
      </Menu>

      <AccountSwitcherMenu anchorEl={accountMenuAnchorEl} onClose={() => setAccountMenuAnchorEl(null)} />

      <Backdrop
        open={isSearchActive}
        onClick={() => setIsSearchActive(false)}
        sx={{ zIndex: theme => theme.zIndex.drawer }}
      />
    </>
  );
};

export default Header;
